import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, WorkHistory, EuroSymbol, AccessTime } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  PieLabelRenderProps,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { BASE_URL } from '../config';

interface Risorsa {
  id: number;
  nome: string;
  cognome: string;
  struttura: string;
  enabled: boolean;
}

interface Kpi {
  gg_rendicontate: number;
  gg_pianificate: number;
  euro_rendicontati: number;
  euro_pianificati: number;
}

interface MensileEntry {
  mese: number;
  gg_rendicontate: number;
  gg_pianificate: number;
  euro_rendicontati: number;
  euro_pianificati: number;
}

interface ProgettoEntry {
  id: number;
  titolo: string;
  pm: string;
  gg_rendicontate: number;
  gg_pianificate: number;
  euro_rendicontati: number;
  euro_pianificati: number;
}

interface RisorsaEntry {
  id: number;
  nome: string;
  cognome: string;
  struttura: string;
  costo_orario: number;
  gg_rendicontate: number;
  gg_pianificate: number;
  euro_rendicontati: number;
  euro_pianificati: number;
}

interface ProgettoItem {
  id: number;
  id_hrp: string | null;
  titolo: string;
  pm: string;
}

interface GruppoProgetti {
  id: number;
  nome: string;
  colore: string;
  progetti: { id: number }[];
}

interface SplitAllocazione {
  gg_investimento: number;
  gg_produttivo: number;
  euro_investimento: number;
  euro_produttivo: number;
}

interface InvestimentiData {
  anno: number;
  anno_precedente: number;
  kpi: Kpi;
  kpi_precedente: Kpi;
  andamento_mensile: MensileEntry[];
  per_progetto: ProgettoEntry[];
  per_risorsa: RisorsaEntry[];
  tutti_progetti: ProgettoItem[];
  split_allocazione: SplitAllocazione | null;
}

const MESI_LABELS: { [key: number]: string } = {
  1: 'GEN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAG', 6: 'GIU',
  7: 'LUG', 8: 'AGO', 9: 'SET', 10: 'OTT', 11: 'NOV', 12: 'DIC',
};

const formatEuro = (v: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

const formatGg = (v: number) => `${v.toLocaleString('it-IT')} gg`;

function DeltaChip({ current, previous, formatter }: { current: number; previous: number; formatter: (v: number) => string }) {
  if (previous === 0 && current === 0) return null;
  const delta = current - previous;
  const perc = previous !== 0 ? Math.round((delta / previous) * 100) : null;
  const isUp = delta > 0;
  const isFlat = delta === 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      {isFlat ? (
        <TrendingFlat sx={{ fontSize: 16, color: 'text.secondary' }} />
      ) : isUp ? (
        <TrendingUp sx={{ fontSize: 16, color: 'warning.main' }} />
      ) : (
        <TrendingDown sx={{ fontSize: 16, color: 'success.main' }} />
      )}
      <Typography variant="caption" color="text.secondary">
        {isFlat ? '=' : isUp ? '+' : ''}{formatter(delta)}
        {perc !== null && ` (${isUp ? '+' : ''}${perc}%)`}
      </Typography>
    </Box>
  );
}

interface KpiCardProps {
  label: string;
  value: number;
  previousValue: number;
  formatter: (v: number) => string;
  icon: React.ReactElement;
  color: string;
  prevAnno: number;
}

function KpiCard({ label, value, previousValue, formatter, icon, color, prevAnno }: KpiCardProps) {
  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ color, mr: 1 }}>{icon}</Box>
          <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
        </Box>
        <Typography variant="h5" fontWeight="bold">{formatter(value)}</Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">{prevAnno}: {formatter(previousValue)}</Typography>
        <DeltaChip current={value} previous={previousValue} formatter={formatter} />
      </CardContent>
    </Card>
  );
}

const InvestimentiPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();
  const userId = user?.id;
  const userStrutture = user?.strutture;
  const hasTutteLeStrutture = user?.hasTutteLeStrutture;

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [data, setData] = useState<InvestimentiData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [selectedRisorse, setSelectedRisorse] = useState<number[]>([]);
  const [selectedStruttura, setSelectedStruttura] = useState<string>('');
  const [selectedProgetti, setSelectedProgetti] = useState<number[]>([]);
  const [progettiSearch, setProgettiSearch] = useState<string>('');
  const [gruppi, setGruppi] = useState<GruppoProgetti[]>([]);
  const [selectedGruppo, setSelectedGruppo] = useState<number | ''>('');

  const years = Array.from({ length: currentYear - 2020 + 3 }, (_, i) => 2020 + i);

  const strutture = useMemo(
    () => Array.from(new Set(risorse.map(r => r.struttura).filter(Boolean))).sort(),
    [risorse]
  );

  // Quando cambia struttura, aggiorna risorse selezionate
  const handleStrutturaChange = (struttura: string) => {
    setSelectedStruttura(struttura);
    if (!struttura) {
      setSelectedRisorse(risorse.map(r => r.id));
    } else {
      setSelectedRisorse(risorse.filter(r => r.struttura === struttura).map(r => r.id));
    }
  };

  // Carica gruppi progetti e imposta di default il gruppo con id=40 se esiste
  useEffect(() => {
    fetch(`${BASE_URL}/gruppi-progetti`)
      .then(r => r.json())
      .then((json: GruppoProgetti[]) => {
        setGruppi(json);
        const defaultGruppo = json.find(g => g.id === 40);
        if (defaultGruppo) {
          setSelectedGruppo(40);
          setSelectedProgetti(defaultGruppo.progetti.map(p => p.id));
        }
      })
      .catch(err => console.error('Errore caricamento gruppi:', err));
  }, []);

  // Quando cambia gruppo, imposta selectedProgetti con tutti i progetti del gruppo
  const handleGruppoChange = (gruppoId: number | '') => {
    setSelectedGruppo(gruppoId);
    setProgettiSearch('');
    if (!gruppoId) {
      setSelectedProgetti([]);
      return;
    }
    const gruppo = gruppi.find(g => g.id === gruppoId);
    if (!gruppo) return;
    setSelectedProgetti(gruppo.progetti.map(p => p.id));
  };

  // Carica risorse
  useEffect(() => {
    const loadRisorse = async () => {
      try {
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (userId) headers['x-user-id'] = userId.toString();
        const res = await fetch(`${BASE_URL}/risorse`, { headers });
        if (!res.ok) throw new Error('Errore caricamento risorse');
        const json = await res.json();
        const attive: Risorsa[] = json.filter((r: Risorsa) => r.enabled);
        let filtrate = attive;
        if (!hasTutteLeStrutture && userStrutture) {
          filtrate = attive.filter(r => userStrutture.includes(r.struttura || ''));
        }
        setRisorse(filtrate);
        setSelectedRisorse(filtrate.map(r => r.id));
      } catch (err) {
        console.error('Errore caricamento risorse:', err);
      }
    };
    loadRisorse();
  }, [userId, userStrutture, hasTutteLeStrutture]);

  // IDs del gruppo selezionato — deve stare prima dell'useEffect che lo usa come dipendenza
  const gruppoProgettiIds = useMemo<Set<number>>(() => {
    if (!selectedGruppo) return new Set();
    const gruppo = gruppi.find(g => g.id === selectedGruppo);
    if (!gruppo) return new Set();
    return new Set(gruppo.progetti.map(p => p.id));
  }, [selectedGruppo, gruppi]);

  // Carica dati investimenti
  useEffect(() => {
    if (selectedRisorse.length === 0) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('risorse', selectedRisorse.join(','));
        if (selectedProgetti.length > 0) params.set('progetti', selectedProgetti.join(','));
        // Passa gli ID del gruppo per il calcolo split investimento vs produttivo
        if (gruppoProgettiIds.size > 0) params.set('investimento_ids', Array.from(gruppoProgettiIds).join(','));

        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (userId) headers['x-user-id'] = userId.toString();

        const res = await fetch(`${BASE_URL}/investimenti/${selectedYear}?${params}`, { headers });
        if (!res.ok) throw new Error('Errore caricamento dati investimenti');
        const json: InvestimentiData = await res.json();
        setData(json);
      } catch (err: any) {
        setError(err.message || 'Errore sconosciuto');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedYear, selectedRisorse, selectedProgetti, userId, gruppoProgettiIds]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
      const entry = data.andamento_mensile.find(e => e.mese === m);
      return {
        mese: MESI_LABELS[m],
        'Rend. gg': entry?.gg_rendicontate ?? 0,
        'Pian. gg': entry?.gg_pianificate ?? 0,
      };
    });
  }, [data]);

  const tuttiProgetti = useMemo(() => data?.tutti_progetti ?? [], [data]);

  const tuttiProgettiFiltrati = useMemo(() => {
    // Se c'è un gruppo selezionato, mostra solo i progetti di quel gruppo
    let lista = selectedGruppo
      ? tuttiProgetti.filter(p => gruppoProgettiIds.has(p.id))
      : tuttiProgetti;
    // Applica ricerca testuale
    if (progettiSearch.trim()) {
      const q = progettiSearch.toLowerCase();
      lista = lista.filter(p =>
        p.titolo.toLowerCase().includes(q) ||
        (p.id_hrp && p.id_hrp.toString().includes(q))
      );
    }
    return lista;
  }, [tuttiProgetti, progettiSearch, selectedGruppo, gruppoProgettiIds]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Dashboard Investimenti
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Monitoraggio gg/uu e costi sui progetti ad investimento
      </Typography>

      {/* Toolbar filtri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Anno */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Anno</InputLabel>
            <Select value={selectedYear} label="Anno" onChange={e => setSelectedYear(Number(e.target.value))}>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Struttura */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Struttura</InputLabel>
            <Select
              value={selectedStruttura}
              label="Struttura"
              onChange={e => handleStrutturaChange(e.target.value as string)}
            >
              <MenuItem value="">Tutte</MenuItem>
              {strutture.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Risorse */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Risorse</InputLabel>
            <Select
              multiple
              value={selectedRisorse}
              onChange={e => setSelectedRisorse(e.target.value as number[])}
              input={<OutlinedInput label="Risorse" />}
              renderValue={selected => `${selected.length} risorse`}
            >
              {risorse.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  <Checkbox checked={selectedRisorse.includes(r.id)} />
                  <ListItemText primary={`${r.cognome} ${r.nome}`} secondary={r.struttura} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Gruppo progetti */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Gruppo</InputLabel>
            <Select
              value={selectedGruppo}
              label="Gruppo"
              onChange={e => handleGruppoChange(e.target.value as number | '')}
            >
              <MenuItem value="">Tutti i gruppi</MenuItem>
              {gruppi.map(g => (
                <MenuItem key={g.id} value={g.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {g.colore && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.colore, flexShrink: 0 }} />}
                    {g.nome}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Progetto */}
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Progetto</InputLabel>
            <Select
              multiple
              value={selectedProgetti}
              onChange={e => setSelectedProgetti(e.target.value as number[])}
              input={<OutlinedInput label="Progetto" />}
              MenuProps={{ autoFocus: false }}
              onClose={() => setProgettiSearch('')}
              renderValue={selected =>
                selected.length === 0
                  ? 'Tutti'
                  : `${selected.length} progett${selected.length === 1 ? 'o' : 'i'}`
              }
            >
              <MenuItem disableRipple disableGutters sx={{ px: 1, py: 0.5 }}>
                <TextField
                  size="small"
                  placeholder="Cerca progetto..."
                  value={progettiSearch}
                  onChange={e => setProgettiSearch(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  fullWidth
                  autoFocus
                />
              </MenuItem>
              {tuttiProgettiFiltrati.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  <Checkbox checked={selectedProgetti.includes(p.id)} />
                  <ListItemText
                    primary={`${p.titolo}${p.id_hrp ? ` (${p.id_hrp})` : ''}`}
                    secondary={p.pm}
                  />
                </MenuItem>
              ))}
              {tuttiProgettiFiltrati.length === 0 && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">Nessun risultato</Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          {/* Riga KPI anno corrente + confronto anno precedente */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Gg Rendicontate"
                value={data.kpi.gg_rendicontate}
                previousValue={data.kpi_precedente.gg_rendicontate}
                formatter={formatGg}
                icon={<WorkHistory />}
                color="#1976d2"
                prevAnno={data.anno_precedente}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Gg Pianificate"
                value={data.kpi.gg_pianificate}
                previousValue={data.kpi_precedente.gg_pianificate}
                formatter={formatGg}
                icon={<AccessTime />}
                color="#ed6c02"
                prevAnno={data.anno_precedente}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Gg Totali"
                value={data.kpi.gg_rendicontate + data.kpi.gg_pianificate}
                previousValue={data.kpi_precedente.gg_rendicontate + data.kpi_precedente.gg_pianificate}
                formatter={formatGg}
                icon={<TrendingUp />}
                color="#2e7d32"
                prevAnno={data.anno_precedente}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="€ Rendicontati"
                value={data.kpi.euro_rendicontati}
                previousValue={data.kpi_precedente.euro_rendicontati}
                formatter={formatEuro}
                icon={<EuroSymbol />}
                color="#1976d2"
                prevAnno={data.anno_precedente}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="€ Pianificati"
                value={data.kpi.euro_pianificati}
                previousValue={data.kpi_precedente.euro_pianificati}
                formatter={formatEuro}
                icon={<EuroSymbol />}
                color="#ed6c02"
                prevAnno={data.anno_precedente}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="€ Totali"
                value={data.kpi.euro_rendicontati + data.kpi.euro_pianificati}
                previousValue={data.kpi_precedente.euro_rendicontati + data.kpi_precedente.euro_pianificati}
                formatter={formatEuro}
                icon={<EuroSymbol />}
                color="#2e7d32"
                prevAnno={data.anno_precedente}
              />
            </Grid>
          </Grid>

          {/* Grafico andamento mensile */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Andamento mensile gg/uu — {data.anno}
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mese" />
                <YAxis unit=" gg" />
                <Tooltip formatter={(v: number) => [`${v} gg`]} />
                <Legend />
                <Bar dataKey="Rend. gg" fill="#1976d2" />
                <Bar dataKey="Pian. gg" fill="#ed6c02" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Grid container spacing={2}>
            {/* Tabella per progetto */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Dettaglio per progetto
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Progetto</TableCell>
                        <TableCell>PM</TableCell>
                        <TableCell align="right">Gg Rend.</TableCell>
                        <TableCell align="right">€ Rend.</TableCell>
                        <TableCell align="right">Gg Pian.</TableCell>
                        <TableCell align="right">€ Pian.</TableCell>
                        <TableCell align="right">€ Tot.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.per_progetto.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary">Nessun dato</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {data.per_progetto.map(p => {
                        const meta = data.tutti_progetti.find(hp => hp.id === p.id);
                        const label = meta?.id_hrp ? `${p.titolo} (${meta.id_hrp})` : p.titolo;
                        return (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" title={label}>{label}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{p.pm}</Typography>
                          </TableCell>
                          <TableCell align="right">{p.gg_rendicontate}</TableCell>
                          <TableCell align="right">{formatEuro(p.euro_rendicontati)}</TableCell>
                          <TableCell align="right">{p.gg_pianificate}</TableCell>
                          <TableCell align="right">{formatEuro(p.euro_pianificati)}</TableCell>
                          <TableCell align="right">
                            {formatEuro(p.euro_rendicontati + p.euro_pianificati)}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                    {data.per_progetto.length > 0 && (() => {
                      const totGgRend  = data.per_progetto.reduce((s, p) => s + p.gg_rendicontate,  0);
                      const totGgPian  = data.per_progetto.reduce((s, p) => s + p.gg_pianificate,   0);
                      const totEuroRend = data.per_progetto.reduce((s, p) => s + p.euro_rendicontati, 0);
                      const totEuroPian = data.per_progetto.reduce((s, p) => s + p.euro_pianificati,  0);
                      return (
                        <TableFooter>
                          <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                            <TableCell colSpan={2}>Totali</TableCell>
                            <TableCell align="right">{Math.round(totGgRend * 10) / 10}</TableCell>
                            <TableCell align="right">{formatEuro(totEuroRend)}</TableCell>
                            <TableCell align="right">{Math.round(totGgPian * 10) / 10}</TableCell>
                            <TableCell align="right">{formatEuro(totEuroPian)}</TableCell>
                            <TableCell align="right">{formatEuro(totEuroRend + totEuroPian)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      );
                    })()}
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Tabella per risorsa */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Dettaglio per risorsa
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Risorsa</TableCell>
                        <TableCell>Struttura</TableCell>
                        <TableCell align="right">Gg Rend.</TableCell>
                        <TableCell align="right">€ Rend.</TableCell>
                        <TableCell align="right">Gg Pian.</TableCell>
                        <TableCell align="right">€ Pian.</TableCell>
                        <TableCell align="right">€ Tot.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.per_risorsa.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary">Nessun dato</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {data.per_risorsa.map(r => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.cognome} {r.nome}</TableCell>
                          <TableCell>
                            <Chip label={r.struttura} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{r.gg_rendicontate}</TableCell>
                          <TableCell align="right">{formatEuro(r.euro_rendicontati)}</TableCell>
                          <TableCell align="right">{r.gg_pianificate}</TableCell>
                          <TableCell align="right">{formatEuro(r.euro_pianificati)}</TableCell>
                          <TableCell align="right">
                            {formatEuro(r.euro_rendicontati + r.euro_pianificati)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    {data.per_risorsa.length > 0 && (() => {
                      const totGgRend   = data.per_risorsa.reduce((s, r) => s + r.gg_rendicontate,  0);
                      const totGgPian   = data.per_risorsa.reduce((s, r) => s + r.gg_pianificate,   0);
                      const totEuroRend = data.per_risorsa.reduce((s, r) => s + r.euro_rendicontati, 0);
                      const totEuroPian = data.per_risorsa.reduce((s, r) => s + r.euro_pianificati,  0);
                      return (
                        <TableFooter>
                          <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                            <TableCell colSpan={2}>Totali</TableCell>
                            <TableCell align="right">{Math.round(totGgRend * 10) / 10}</TableCell>
                            <TableCell align="right">{formatEuro(totEuroRend)}</TableCell>
                            <TableCell align="right">{Math.round(totGgPian * 10) / 10}</TableCell>
                            <TableCell align="right">{formatEuro(totEuroPian)}</TableCell>
                            <TableCell align="right">{formatEuro(totEuroRend + totEuroPian)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      );
                    })()}
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Grafico ripartizione investimento vs produttivo */}
          {data.split_allocazione ? (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Ripartizione gg/uu — Investimento vs Produttivo ({data.anno})
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quota del lavoro delle risorse selezionate sui progetti del gruppo "{gruppi.find(g => g.id === selectedGruppo)?.nome ?? 'selezionato'}" rispetto a tutti gli altri progetti.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <ResponsiveContainer width={280} height={220}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Investimento', value: data.split_allocazione.gg_investimento },
                        { name: 'Produttivo',   value: data.split_allocazione.gg_produttivo   },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      dataKey="value"
                      label={({ name, percent }: PieLabelRenderProps) => `${name} ${(((percent as number) ?? 0) * 100).toFixed(1)}%`}
                      labelLine={true}
                    >
                      <Cell fill="#ed6c02" />
                      <Cell fill="#1976d2" />
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        const split = data.split_allocazione!;
                        const euro = name === 'Investimento' ? split.euro_investimento : split.euro_produttivo;
                        return [`${value} gg  |  ${formatEuro(euro)}`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#ed6c02', flexShrink: 0 }} />
                    <Typography variant="body2"><strong>Investimento:</strong> {data.split_allocazione.gg_investimento} gg — {formatEuro(data.split_allocazione.euro_investimento)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#1976d2', flexShrink: 0 }} />
                    <Typography variant="body2"><strong>Produttivo:</strong> {data.split_allocazione.gg_produttivo} gg — {formatEuro(data.split_allocazione.euro_produttivo)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Totale: {data.split_allocazione.gg_investimento + data.split_allocazione.gg_produttivo} gg — {formatEuro(data.split_allocazione.euro_investimento + data.split_allocazione.euro_produttivo)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ) : (
            selectedGruppo ? null : (
              <Alert severity="info" sx={{ mt: 2 }}>
                Seleziona un gruppo per visualizzare la ripartizione investimento vs produttivo.
              </Alert>
            )
          )}
        </>
      )}

      {!loading && !data && !error && (
        <Alert severity="info">Seleziona almeno una risorsa per visualizzare i dati.</Alert>
      )}
    </Box>
  );
};

export default InvestimentiPage;
